<template>
    <v-container class="py-12">
        <v-row>
            <v-col cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">Fechamento #{{ $route.params.id }}</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="[
                            { text: 'Home', to: '/home' },
                            { text: 'Motoristas a pagar', to: '/financeiro/motoristas', exact: true },
                            { text: `Fechamento #${$route.params.id}`, disabled: true }
                        ]" />
                    </div>
                    <div class="button-group" style="padding-right: 16px;">
                        <v-btn outlined color="primary" elevation="0" :loading="busy.export" @click="exportPayout">
                            <v-icon left small>mdi-file-excel-outline</v-icon>Extrato
                        </v-btn>
                        <v-btn outlined color="primary" elevation="0" to="/financeiro/motoristas">Voltar</v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-progress-linear v-if="loading" indeterminate color="primary" />

        <template v-if="payout">
            <!-- CABEÇALHO -->
            <v-card outlined class="mb-4">
                <v-card-text>
                    <div class="header-row">
                        <div>
                            <h2 class="mb-1">{{ driverName }}</h2>
                            <v-chip x-small outlined :color="isPartner ? 'orange darken-2' : 'primary'">
                                {{ isPartner ? 'Parceiro' : 'Funcionário' }}
                            </v-chip>
                            <span class="body-2 ml-2">Período {{ date(payout.period_start) }} a {{ date(payout.period_end) }}</span>
                        </div>
                        <v-chip dark :color="statusColor(payout.status)">{{ statusName(payout.status) }}</v-chip>
                    </div>

                    <div class="summary">
                        <div>
                            <small>{{ isPartner ? `Viagens (${payout.trips_count})` : 'Salário' }}</small>
                            <strong>{{ money(isPartner ? payout.trips_amount : payout.base_salary) }}</strong>
                        </div>
                        <div>
                            <small>Acréscimos</small>
                            <strong class="green--text">+ {{ money(payout.additions_amount) }}</strong>
                        </div>
                        <div>
                            <small>Descontos</small>
                            <strong class="red--text">− {{ money(payout.deductions_amount) }}</strong>
                        </div>
                        <div class="summary__total">
                            <small>Total a pagar</small>
                            <strong>{{ money(payout.total_amount) }}</strong>
                        </div>
                        <div>
                            <small>Custo para a empresa</small>
                            <strong>{{ money(payout.company_cost) }}</strong>
                            <span v-if="!isPartner && Number(payout.charges_percent)" class="caption grey--text">
                                inclui {{ Number(payout.charges_percent) }}% de encargos
                            </span>
                        </div>
                    </div>

                    <v-alert v-if="payout.pending_count && payout.status === 'open'" dense text type="warning" class="mt-3 mb-0 body-2">
                        {{ payout.pending_count }} viagem(ns) sem preço para a localidade. Informe o valor abaixo antes de conferir.
                    </v-alert>

                    <v-alert v-if="payout.status === 'paid'" dense text type="success" class="mt-3 mb-0 body-2">
                        Pago em {{ date(payout.paid_at) }} · {{ money(payout.paid_amount) }}
                        <template v-if="payout.payment_method"> · {{ payout.payment_method }}</template>
                        <v-btn v-if="payout.receipt_resource_id" x-small text color="primary" class="ml-2" @click="downloadReceipt">
                            <v-icon x-small left>mdi-paperclip</v-icon>Comprovante
                        </v-btn>
                    </v-alert>

                    <!-- AÇÕES -->
                    <div class="actions mt-4">
                        <template v-if="payout.status === 'open' && canManage">
                            <v-btn v-if="isPartner" outlined small :loading="busy.refresh" @click="run('refresh')">
                                <v-icon left small>mdi-refresh</v-icon>Atualizar viagens
                            </v-btn>
                            <v-btn color="primary" elevation="0" small :loading="busy.review" @click="run('review')">
                                <v-icon left small>mdi-check</v-icon>Marcar como conferido
                            </v-btn>
                        </template>
                        <template v-if="payout.status === 'reviewed'">
                            <v-btn v-if="canPay" color="success" elevation="0" small @click="openPay">
                                <v-icon left small>mdi-cash-check</v-icon>Registrar pagamento
                            </v-btn>
                            <v-btn v-if="canManage" outlined small :loading="busy.reopen" @click="run('reopen')">Reabrir</v-btn>
                        </template>
                        <v-btn v-if="payout.status === 'paid' && canPay" outlined small color="orange darken-2"
                            :loading="busy.unpay" @click="confirmRun('unpay', 'Estornar o pagamento? O fechamento volta para conferido.')">
                            Estornar pagamento
                        </v-btn>
                        <v-btn v-if="['open', 'reviewed'].includes(payout.status) && canManage" text small color="error"
                            @click="confirmRun('cancel', 'Cancelar o fechamento? As viagens voltam a ficar disponíveis para outro fechamento.')">
                            Cancelar fechamento
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>

            <v-row>
                <!-- VIAGENS (parceiro) -->
                <v-col v-if="isPartner" cols="12" md="8">
                    <v-card outlined>
                        <v-card-title class="subtitle-1 font-weight-bold">Viagens</v-card-title>
                        <v-data-table dense :headers="itemHeaders" :items="payout.items" :items-per-page="50"
                            class="app-table" no-data-text="Nenhuma viagem" :item-class="itemClass">
                            <template v-slot:[`item.trip_date`]="{ item }">{{ date(item.trip_date) }} {{ item.trip_time || '' }}</template>
                            <template v-slot:[`item.trip_id`]="{ item }">#{{ item.trip_id }}</template>
                            <template v-slot:[`item.location_name`]="{ item }">
                                {{ item.location_name || 'Sem localidade' }}
                                <div class="caption grey--text text-truncate" style="max-width: 260px" :title="item.itinerary">{{ item.itinerary }}</div>
                            </template>
                            <template v-slot:[`item.value`]="{ item }">
                                <div v-if="editable && editingItem === item.id" class="d-flex align-center">
                                    <v-currency-field dense outlined hide-details prefix="R$" v-model="itemValue" style="max-width: 130px" />
                                    <v-btn icon x-small color="primary" :loading="busy.item" @click="saveItem(item)"><v-icon small>mdi-check</v-icon></v-btn>
                                    <v-btn icon x-small @click="editingItem = null"><v-icon small>mdi-close</v-icon></v-btn>
                                </div>
                                <template v-else>
                                    <span v-if="item.value === null" class="orange--text text--darken-2 font-weight-bold">Sem preço</span>
                                    <span v-else>{{ money(item.value) }}</span>
                                    <v-chip v-if="item.manual" x-small outlined class="ml-1">manual</v-chip>
                                    <v-btn v-if="editable" icon x-small @click="editItem(item)"><v-icon x-small>mdi-pencil-outline</v-icon></v-btn>
                                </template>
                            </template>
                            <template v-slot:[`item.actions`]="{ item }">
                                <v-btn v-if="editable" icon x-small color="error" title="Tirar do fechamento" @click="removeItem(item)">
                                    <v-icon small>mdi-close-circle-outline</v-icon>
                                </v-btn>
                            </template>
                        </v-data-table>
                    </v-card>
                </v-col>

                <!-- AJUSTES -->
                <v-col cols="12" :md="isPartner ? 4 : 8">
                    <v-card outlined>
                        <v-card-title class="subtitle-1 font-weight-bold">Ajustes</v-card-title>
                        <v-card-text>
                            <div v-if="!payout.adjustments.length" class="body-2 grey--text mb-3">Nenhum ajuste.</div>
                            <div v-for="adjustment in payout.adjustments" :key="adjustment.id" class="adjustment">
                                <div>
                                    <strong>{{ adjustmentName(adjustment.type) }}</strong>
                                    <div class="caption grey--text">{{ adjustment.description || '' }}</div>
                                </div>
                                <span :class="isDeduction(adjustment.type) ? 'red--text' : 'green--text'">
                                    {{ isDeduction(adjustment.type) ? '−' : '+' }} {{ money(adjustment.amount) }}
                                </span>
                                <v-btn v-if="editable" icon x-small color="error" @click="removeAdjustment(adjustment)">
                                    <v-icon small>mdi-delete-outline</v-icon>
                                </v-btn>
                            </div>

                            <v-form v-if="editable" ref="adjustmentForm" class="mt-3">
                                <v-select dense outlined hide-details="auto" label="Tipo" :items="adjustmentTypes"
                                    v-model="adjustment.type" class="mb-2" :rules="[v => !!v || 'Informe o tipo']" />
                                <v-currency-field dense outlined hide-details="auto" prefix="R$" label="Valor"
                                    v-model="adjustment.amount" class="mb-2" :rules="[v => Number(v) > 0 || 'Informe o valor']" />
                                <v-text-field dense outlined hide-details label="Descrição" v-model="adjustment.description" class="mb-2" />
                                <v-btn small outlined color="primary" :loading="busy.adjustment" @click="addAdjustment">
                                    <v-icon left small>mdi-plus</v-icon>Adicionar ajuste
                                </v-btn>
                            </v-form>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </template>

        <!-- PAGAMENTO -->
        <v-dialog v-model="pay.open" max-width="480px">
            <v-card>
                <v-card-title class="custom-modal-title text-uppercase black--text font-weight-bold">Registrar pagamento</v-card-title>
                <v-card-text>
                    <v-form ref="payForm">
                        <v-text-field dense outlined type="date" label="Data do pagamento" v-model="pay.paid_at"
                            :rules="[v => !!v || 'Informe a data']" />
                        <v-currency-field dense outlined prefix="R$" label="Valor pago" v-model="pay.paid_amount" />
                        <v-select dense outlined clearable label="Forma de pagamento" :items="paymentMethods" v-model="pay.payment_method" />
                        <v-file-input dense outlined label="Comprovante (opcional)" prepend-icon="" prepend-inner-icon="mdi-paperclip"
                            accept="image/*,application/pdf" v-model="pay.file" />
                        <v-textarea dense outlined rows="2" label="Observação" v-model="pay.notes" />
                    </v-form>
                    <p v-if="Number(pay.paid_amount) !== Number(payout && payout.total_amount)" class="caption orange--text text--darken-2 mb-0">
                        Valor diferente do total a pagar ({{ money(payout && payout.total_amount) }}).
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="pay.open = false">Cancelar</v-btn>
                    <v-btn color="success" elevation="0" :loading="busy.pay" @click="confirmPay">Confirmar pagamento</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment'

const STATUS = {
    open: { text: 'Aberto', color: 'blue' },
    reviewed: { text: 'Conferido', color: 'purple' },
    paid: { text: 'Pago', color: 'green' },
    cancelled: { text: 'Cancelado', color: 'grey' }
}

const ADJUSTMENTS = [
    { value: 'bonus', text: 'Bônus', deduction: false },
    { value: 'refund', text: 'Reembolso', deduction: false },
    { value: 'other_addition', text: 'Outro acréscimo', deduction: false },
    { value: 'advance', text: 'Adiantamento (desconta)', deduction: true },
    { value: 'discount', text: 'Desconto', deduction: true },
    { value: 'other_deduction', text: 'Outro desconto', deduction: true }
]

export default {
    name: 'DriverPayoutDetails',

    data: () => ({
        loading: false,
        payout: null,
        busy: { refresh: false, review: false, reopen: false, unpay: false, cancel: false, pay: false, item: false, adjustment: false, export: false },
        editingItem: null,
        itemValue: null,
        adjustment: { type: null, amount: null, description: '' },
        adjustmentTypes: ADJUSTMENTS,
        paymentMethods: ['PIX', 'Transferência', 'Dinheiro', 'Boleto', 'Outro'],
        pay: { open: false, paid_at: null, paid_amount: null, payment_method: null, file: null, notes: '' },
        itemHeaders: [
            { text: 'Data', value: 'trip_date', sortable: false, width: 140 },
            { text: 'OS', value: 'trip_id', sortable: false, width: 80 },
            { text: 'Localidade', value: 'location_name', sortable: false },
            { text: 'Valor', value: 'value', sortable: false, width: 190 },
            { text: '', value: 'actions', sortable: false, width: 40 }
        ]
    }),

    computed: {
        isPartner() {
            return this.payout && Number(this.payout.driver_type) === 2
        },
        driverName() {
            const driver = this.payout && this.payout.driver
            return driver ? (driver.nickname || driver.name) : 'Motorista'
        },
        canManage() {
            return this.$check(['financial.drivers.manage'])
        },
        canPay() {
            return this.$check(['financial.drivers.pay'])
        },
        editable() {
            return this.payout && this.payout.status === 'open' && this.canManage
        }
    },

    created() {
        this.load()
    },

    methods: {
        url(path = '') {
            return `/api/v2/financial/driver-payouts/${this.$route.params.id}${path}`
        },

        async load() {
            this.loading = true
            try {
                const response = await axios.get(this.url())
                this.payout = response.data.content
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
            } finally {
                this.loading = false
            }
        },

        async request(key, method, path, payload) {
            this.busy[key] = true
            try {
                const response = await axios[method](this.url(path), payload)
                this.payout = response.data.content
                return true
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 6000 })
                return false
            } finally {
                this.busy[key] = false
            }
        },

        async run(action) {
            const messages = { refresh: 'Viagens atualizadas', review: 'Fechamento conferido', reopen: 'Fechamento reaberto', unpay: 'Pagamento estornado', cancel: 'Fechamento cancelado' }
            if (await this.request(action, 'post', `/${action}`)) {
                this.$dialog.notify.success(messages[action], { position: 'top-right', timeout: 3000 })
            }
        },

        async confirmRun(action, text) {
            const confirmed = await this.$dialog.confirm({ title: 'Confirmar', text, actions: { false: 'Voltar', true: 'Confirmar' } })
            if (confirmed) this.run(action)
        },

        editItem(item) {
            this.editingItem = item.id
            this.itemValue = item.value === null ? null : Number(item.value)
        },

        async saveItem(item) {
            if (await this.request('item', 'patch', `/items/${item.id}`, { value: this.itemValue })) this.editingItem = null
        },

        async removeItem(item) {
            const confirmed = await this.$dialog.confirm({
                title: 'Tirar do fechamento',
                text: `Tirar a OS #${item.trip_id} deste fechamento? Ela volta a ficar disponível para outro fechamento.`,
                actions: { false: 'Voltar', true: 'Tirar' }
            })
            if (confirmed) this.request('item', 'delete', `/items/${item.id}`)
        },

        async addAdjustment() {
            if (!this.$refs.adjustmentForm.validate()) return
            if (await this.request('adjustment', 'post', '/adjustments', this.adjustment)) {
                this.adjustment = { type: null, amount: null, description: '' }
                this.$refs.adjustmentForm.resetValidation()
            }
        },

        async removeAdjustment(adjustment) {
            const confirmed = await this.$dialog.confirm({
                title: 'Remover ajuste',
                text: `Remover ${this.adjustmentName(adjustment.type).toLowerCase()} de ${this.money(adjustment.amount)}?`,
                actions: { false: 'Voltar', true: 'Remover' }
            })
            if (confirmed) this.request('adjustment', 'delete', `/adjustments/${adjustment.id}`)
        },

        openPay() {
            this.pay = {
                open: true,
                paid_at: moment().format('YYYY-MM-DD'),
                paid_amount: Number(this.payout.total_amount),
                payment_method: null,
                file: null,
                notes: this.payout.notes || ''
            }
        },

        async confirmPay() {
            if (!this.$refs.payForm.validate()) return

            this.busy.pay = true
            let receiptId = null

            try {
                if (this.pay.file) {
                    const formData = new FormData()
                    formData.append('file', this.pay.file)
                    const upload = await axios.post('/api/v2/resources', formData)
                    receiptId = upload.data.content && upload.data.content.id
                }
            } catch (error) {
                this.busy.pay = false
                this.$dialog.notify.error('Não foi possível enviar o comprovante', { position: 'top-right', timeout: 5000 })
                return
            }

            this.busy.pay = false

            const ok = await this.request('pay', 'post', '/pay', {
                paid_at: this.pay.paid_at,
                paid_amount: this.pay.paid_amount,
                payment_method: this.pay.payment_method,
                receipt_resource_id: receiptId,
                notes: this.pay.notes
            })

            if (ok) {
                this.pay.open = false
                this.$dialog.notify.success('Pagamento registrado', { position: 'top-right', timeout: 3000 })
            }
        },

        async downloadReceipt() {
            try {
                const response = await axios.get(`/api/v2/resources/${this.payout.receipt_resource_id}`, { responseType: 'blob' })
                const url = URL.createObjectURL(response.data)
                window.open(url, '_blank')
            } catch (error) {
                this.$dialog.notify.error('Não foi possível abrir o comprovante', { position: 'top-right', timeout: 4000 })
            }
        },

        async exportPayout() {
            this.busy.export = true
            try {
                const response = await axios.get(this.url('/export'), { responseType: 'blob' })
                const link = document.createElement('a')
                link.href = URL.createObjectURL(new Blob([response.data]))
                link.download = `extrato-${this.driverName}-${this.payout.period_start}.xlsx`
                link.click()
                URL.revokeObjectURL(link.href)
            } catch (error) {
                this.$dialog.notify.error('Não foi possível gerar o extrato', { position: 'top-right', timeout: 4000 })
            } finally {
                this.busy.export = false
            }
        },

        itemClass(item) {
            return item.value === null ? 'pending-row' : ''
        },

        adjustmentName(type) {
            const found = ADJUSTMENTS.find(item => item.value === type)
            return found ? found.text.replace(' (desconta)', '') : type
        },

        isDeduction(type) {
            const found = ADJUSTMENTS.find(item => item.value === type)
            return !!(found && found.deduction)
        },

        statusName(status) {
            return (STATUS[status] || {}).text || status
        },

        statusColor(status) {
            return (STATUS[status] || {}).color || 'grey'
        },

        money(value) {
            return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        },

        date(value) {
            return value ? moment.utc(value).format('DD/MM/YYYY') : '-'
        }
    }
}
</script>

<style lang="scss" scoped>
.header-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
}

.summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 14px;
    margin-top: 16px;

    div {
        display: flex;
        flex-direction: column;
    }

    small {
        color: #5a6b85;
        font-size: 12px;
    }

    strong {
        font-size: 18px;
    }
}

.summary__total strong {
    font-size: 22px;
    color: #1450b5;
}

.actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.adjustment {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 0;
    border-bottom: 1px solid #eef1f6;
}

::v-deep .pending-row {
    background: #fff6ec;
}
</style>
